import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../css/CreateNotification.css';
import api from '../utils/axiosConfig';

const CreateNotificationUser = () => {
  const [formData, setFormData] = useState({
    userCode: '',
    sessionName: '',
    startDate: null,
    singleTime: null,
    endDate: null,
    notificationTime: null
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [linkedParticipants, setLinkedParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    const fetchLinkedParticipants = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Nicht authentifiziert');
        setLoading(false);
        return;
      }

      try {
        // 1. Get current user's code prefix
        const decoded = jwtDecode(token);
        const userResponse = await api.get(`/api/users/${decoded._id}`);
        const userCodePrefix = userResponse.data.code.slice(0, 5);

        // 2. Get all participants with matching code prefix
        const participantsResponse = await api.get(`/api/participants`);

        // 3. Filter participants by code prefix
        const filteredParticipants = participantsResponse.data.filter(
          participant => participant.code.startsWith(userCodePrefix)
        );

        setLinkedParticipants(filteredParticipants);
        
        // 4. Auto-select first participant if available
        if (filteredParticipants.length > 0) {
          setFormData(prev => ({ 
            ...prev, 
            userCode: filteredParticipants[0].code 
          }));
        }

        setLoading(false);
      } catch (err) {
        setError('Fehler beim Laden der Teilnehmer');
        setLoading(false);
        console.error('Error fetching participants:', err);
      }
    };

    fetchLinkedParticipants();
  }, []);

  const handleDateChange = (date, field) => {
    setFormData(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const handleTimeChange = (time, field) => {
    setFormData(prev => ({
      ...prev,
      [field]: time
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    try {
      if (isRecurring) {
        // Create notifications for each weekday in the range
        const notifications = [];
        let currentDate = new Date(formData.startDate);
        const endDate = new Date(formData.endDate);
        
        while (currentDate <= endDate) {
          if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) { // Skip weekends
            const scheduledAt = new Date(currentDate);
            scheduledAt.setHours(formData.notificationTime.getHours());
            scheduledAt.setMinutes(formData.notificationTime.getMinutes());
            
            notifications.push({
              userCode: formData.userCode,
              sessionName: formData.sessionName,
              scheduledAt: scheduledAt.toISOString()
            });
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }

        if (notifications.length === 0) {
          throw new Error('Keine Wochentage im ausgewählten Zeitraum');
        }

        await Promise.all(
          notifications.map(notification => 
            api.post('/api/notifications', notification))
        );

        setSuccess(true);
        setTimeout(() => navigate('/my-notifications'), 1500);
      } else {
        // Single notification
        const scheduledAt = new Date(formData.startDate);
        scheduledAt.setHours(formData.singleTime.getHours());
        scheduledAt.setMinutes(formData.singleTime.getMinutes());

        if (scheduledAt.getDay() === 0 || scheduledAt.getDay() === 6) {
          throw new Error('Benachrichtigungen können nicht am Wochenende erstellt werden');
        }

        await api.post(`/api/notifications`, {
          userCode: formData.userCode,
          sessionName: formData.sessionName,
          scheduledAt: scheduledAt.toISOString()
        });
        
        setSuccess(true);
        setTimeout(() => navigate('/my-notifications'), 1500);
      }
    } catch (err) {
      setError(err.message || err.response?.data?.error || 'Fehler beim Erstellen der Benachrichtigung(en)');
    }
  };

  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const res = await api.get(`/api/sessions/list`);
        setSessions(res.data); // assuming backend returns an array of session names
      } catch (err) {
        console.error("Fehler beim Laden der Sessions:", err);
      }
    };
    fetchSessions();
  }, []);

  if (loading) return <div className="loading">Lade Teilnehmer...</div>;

  return (
    <div className="notification-form-container">
      <h1>Neuen Zeitplan erstellen</h1>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">
        {isRecurring ? 'Benachrichtigungen erfolgreich erstellt!' : 'Benachrichtigung erfolgreich erstellt!'}
      </div>}

      <form onSubmit={handleSubmit} className="notification-form">
        {/* Participant selection (same as before) */}
        <div className="form-group">
          <label>Teilnehmer</label>
          <select
            value={formData.userCode}
            onChange={(e) => setFormData({...formData, userCode: e.target.value})}
            required
          >
            {linkedParticipants.map(p => (
              <option key={p._id} value={p.code}>
                {p.code} {p.name && `- ${p.name}`}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="sessionName">Session Name</label>
          <select
            id="sessionName"
            name="sessionName"
            value={formData.sessionName}
            onChange={handleChange}
            required
          >
            <option value="">-- Bitte wählen --</option>
            {sessions.map((session, index) => (
              <option key={index} value={session.name || session}>
                {session.name || session}
              </option>
            ))}
          </select>
        </div>

        {/* Schedule type toggle (improved) */}
        <div className="form-group switch-group">
          <label className="switch-label">Zeitplan-Typ</label>
          <div className="switch-wrapper">
            <span className={!isRecurring ? 'active' : ''}>Einzeltag</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={isRecurring}
                onChange={() => setIsRecurring(!isRecurring)}
              />
              <span className="slider round"></span>
            </label>
            <span className={isRecurring ? 'active' : ''}>Mehrere Tage</span>
          </div>
        </div>

        {isRecurring ? (
          <>
            <div className="form-group">
              <label>Zeitraum</label>
              <div className="date-range-picker">
                <DatePicker
                  selected={formData.startDate}
                  onChange={(date) => handleDateChange(date, 'startDate')}
                  selectsStart
                  startDate={formData.startDate}
                  endDate={formData.endDate}
                  minDate={new Date()}
                  excludeDates={[0, 6]}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="Startdatum wählen"
                  className="date-input"
                  required
                />
                <span className="date-range-separator">bis</span>
                <DatePicker
                  selected={formData.endDate}
                  onChange={(date) => handleDateChange(date, 'endDate')}
                  selectsEnd
                  startDate={formData.startDate}
                  endDate={formData.endDate}
                  minDate={formData.startDate}
                  excludeDates={[0, 6]}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="Enddatum wählen"
                  className="date-input"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Uhrzeit (24h Format)</label>
              <DatePicker
                selected={formData.notificationTime}
                onChange={(time) => handleTimeChange(time, 'notificationTime')}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={10}
                timeCaption="Uhrzeit"
                dateFormat="HH:mm"
                timeFormat="HH:mm"
                placeholderText="Uhrzeit wählen (z.B. 14:30)"
                className="time-input"
                required
              />
            </div>

            <div className="info-message">
              Hinweis: Wochenenden (Samstag/Sonntag) werden automatisch übersprungen.
            </div>
          </>
        ) : (
          <>
            <div className="form-group">
              <label>Datum & Uhrzeit</label>
              <DatePicker
                selected={formData.startDate}
                onChange={(date) => handleDateChange(date, 'startDate')}
                minDate={new Date()}
                excludeDates={[0, 6]}
                dateFormat="dd.MM.yyyy"
                placeholderText="Datum wählen"
                className="date-input"
                required
              />
              <DatePicker
                selected={formData.singleTime}
                onChange={(time) => handleTimeChange(time, 'singleTime')}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={10}
                timeCaption="Uhrzeit"
                dateFormat="HH:mm"
                timeFormat="HH:mm"
                placeholderText="Uhrzeit wählen (z.B. 14:30)"
                className="time-input"
                required
              />
            </div>
          </>
        )}

        <div className="button-group">
          <button type="submit" className="submit-button">
            Zeitplan erstellen
          </button>
          <button 
            type="button" 
            className="cancel-button"
            onClick={() => navigate('/my-notifications')}
          >
            Abbrechen
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateNotificationUser;